/**
 * Popup de messages internes.
 * Verifie les messages non lus toutes les 30 secondes.
 * Affiche une modale que le destinataire doit accuser reception.
 */
import React, { useState, useEffect } from 'react';
import { useGetNonLusQuery, useMarquerLuMutation, MessageInterne } from '../../services/messagerieApi';
import { Info, AlertTriangle, AlertOctagon, Award, Clock, X, Check } from 'lucide-react';

const TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string; bgHeader: string }> = {
  info: { icon: <Info size={24} />, color: 'text-blue-600', bgHeader: 'bg-blue-600' },
  important: { icon: <AlertTriangle size={24} />, color: 'text-orange-600', bgHeader: 'bg-orange-600' },
  urgent: { icon: <AlertOctagon size={24} />, color: 'text-red-600', bgHeader: 'bg-red-600' },
  felicitation: { icon: <Award size={24} />, color: 'text-green-600', bgHeader: 'bg-green-600' },
  rappel: { icon: <Clock size={24} />, color: 'text-purple-600', bgHeader: 'bg-purple-600' },
};

const MessagePopup: React.FC = () => {
  const { data, refetch } = useGetNonLusQuery(undefined, { pollingInterval: 30000 });
  const [marquerLu] = useMarquerLuMutation();
  const [currentMessage, setCurrentMessage] = useState<MessageInterne | null>(null);
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());

  // Afficher le premier message non lu non dismiss
  useEffect(() => {
    if (data && data.messages.length > 0 && !currentMessage) {
      const premier = data.messages.find(m => !dismissed.has(m.id));
      if (premier) setCurrentMessage(premier);
    }
  }, [data, currentMessage, dismissed]);

  const handleAccuserReception = async () => {
    if (!currentMessage) return;
    try {
      await marquerLu(currentMessage.id).unwrap();
    } catch { /* ignore */ }
    setDismissed(prev => new Set(prev).add(currentMessage.id));
    setCurrentMessage(null);
    refetch();
  };

  if (!currentMessage) return null;

  const config = TYPE_CONFIG[currentMessage.type_message] || TYPE_CONFIG.info;

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-[bounceIn_0.3s_ease-out]">
        {/* Header colore */}
        <div className={`${config.bgHeader} px-6 py-4 flex items-center gap-3`}>
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white">
            {config.icon}
          </div>
          <div className="flex-1">
            <div className="text-white font-bold text-sm">{currentMessage.type_display || currentMessage.type_message}</div>
            <div className="text-white/80 text-xs">De : {currentMessage.expediteur_nom}</div>
          </div>
          {currentMessage.type_message !== 'urgent' && (
            <button onClick={handleAccuserReception} className="text-white/60 hover:text-white">
              <X size={18} />
            </button>
          )}
        </div>

        {/* Corps */}
        <div className="p-6">
          <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100 mb-3">{currentMessage.sujet}</h3>
          <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
            {currentMessage.contenu}
          </div>
          <div className="mt-4 text-xs text-gray-400">
            {new Date(currentMessage.date_envoi).toLocaleString('fr-FR')}
            {currentMessage.est_broadcast && <span className="ml-2 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">Message general</span>}
          </div>
        </div>

        {/* Action */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900">
          <button
            onClick={handleAccuserReception}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 ${config.bgHeader} text-white rounded-lg font-medium text-sm hover:opacity-90`}
          >
            <Check size={16} /> J'ai lu ce message
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessagePopup;
